import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useBarcode } from "../../../../workers/useBarcode";
import useWorkers from "../../../../workers/useWorkers";
import { patchClaim, patchMeta } from "../actions";
import { isLabelSide } from "../constants";
import {
    convertStringToJSONObject,
    getRegex,
    isCordCylinder,
    MULTI_LINE_SERIAL,
    preprocessText,
} from "./scanConfig";

export function useScanPipeline({ side, meta, webcamRef }) {
    const post = useSelector(state => state.addclaim.post);
    const labels = useSelector(state => state.addclaim.labels);
    const user = useSelector(state => state.auth.user);
    const dispatch = useDispatch();

    const canvasRef = useRef(null);
    const scanForBarcode = useBarcode(canvasRef);
    const regexLengthRef = useRef(0);
    const jobQueueLengthRef = useRef(0);

    const [regex, setRegexState] = useState([]);
    const [transformations, setTransformations] = useState([]);
    const [regexParts, setRegexParts] = useState({ AB: {}, the_number: {} });
    const [selectedRegexParts, setSelectedRegexParts] = useState({});
    const [camera_results, setCameraResults] = useState(["Waiting for video..."]);

    const isLabel = isLabelSide(side);

    const handleWorkerMessage = useCallback((event) => {
        const text = event?.text;
        if (!text) return;

        if (side === "COMPASS_QR-label") {
            const parsedData = convertStringToJSONObject(text);
            const updatedData = {
                warranty_request: parsedData.RequestClaimID,
                item_number: parsedData.ItemID,
                a_number: parsedData.ClaimLineID,
                work_date: parsedData.WorkDate,
            };
            dispatch(patchClaim(updatedData));
            setRegexState(prev => prev.map(i => ({ ...i, result: updatedData?.[i.field] || i.result })));
            return;
        }

        const filteredText = preprocessText(text, isLabel);
        setCameraResults(prev => [...prev.slice(Math.max(prev.length - 2, 0)), filteredText]);

        setRegexState(prev => prev.map(i => {
            if (i.result) return i;
            if (MULTI_LINE_SERIAL.includes(i.item_number)) {
                const ABMatches = [...filteredText.matchAll(/\b([RSTUVWXYZAB][A-L])\b/g)].map(m => m[1]);
                const numberMatches = [...filteredText.matchAll(/(\d{7,8})/g)].map(m => m[1]);
                if (ABMatches.length > 0) {
                    setRegexParts(p => {
                        const updatedAB = ABMatches.reduce((acc, item) => ({ ...acc, [item]: (acc[item] || 0) + 1 }), { ...p.AB });
                        return { ...p, AB: Object.fromEntries(Object.entries(updatedAB).sort((a, b) => b[1] - a[1]).slice(0, 14)) };
                    });
                }
                if (numberMatches.length > 0) {
                    setRegexParts(p => {
                        const updatedNumbers = numberMatches.reduce((acc, item) => ({ ...acc, [item]: (acc[item] || 0) + 1 }), { ...p.the_number });
                        return { ...p, the_number: Object.fromEntries(Object.entries(updatedNumbers).sort((a, b) => b[1] - a[1]).slice(0, 14)) };
                    });
                }
                return i;
            }

            let result = false;
            if (isCordCylinder(post.item_number)) {
                const array = filteredText.split(/\D+/).filter(Boolean);
                if (array.length === 3) result = array[1].match(i.regex);
            } else {
                result = filteredText.match(i.regex);
            }
            if (result) {
                result = result[0].replace(i.regex, i.cleaning);
                if (i.field) {
                    if (isLabel) dispatch(patchClaim({ [i.field]: result }));
                    if (meta?.use_OCR) dispatch(patchMeta(meta.name, result));
                }
                return { ...i, result };
            }
            return i;
        }));
    }, [dispatch, isLabel, meta?.use_OCR, meta?.name, post.item_number, side]);

    const { addJob, jobQueue, processedImage } = useWorkers(handleWorkerMessage, jobQueueLengthRef, 4, transformations);

    useEffect(() => {
        const timer = setInterval(async () => {
            if (!webcamRef?.current || regexLengthRef.current === 0) return;
            const screenshot = webcamRef.current.getScreenshot();
            if (screenshot) {
                if (jobQueueLengthRef.current < 3 && Math.random() < 0.7) {
                    if (!(isCordCylinder(post.item_number) && !isLabel)) {
                        addJob({ screenshot });
                    }
                }
                const text = await scanForBarcode(screenshot);
                if (text) handleWorkerMessage({ text });
            }
        }, 250);
        return () => clearInterval(timer);
    }, [addJob, handleWorkerMessage, scanForBarcode, webcamRef, post.item_number, isLabel]);

    useEffect(() => {
        if (!isLabel || post.step === "validate_label") return;
        const updates = {};
        if (side === 'SMART-label' && post.a_number?.includes('-') && !post.warranty_request) {
            updates.warranty_request = 'C' + post.a_number.slice(1, post.a_number.indexOf('-'));
        }
        const hasWarranty = user?.center?.is_lna ? true : !!updates.warranty_request || !!post.warranty_request;
        const isLabelComplete = post.a_number && hasWarranty && post.item_number;
        if (isLabelComplete) {
            updates.step = "validate_label";
        }
        if (Object.keys(updates).length > 0) {
            dispatch(patchClaim(updates));
        }
    }, [dispatch, isLabel, post.a_number, post.item_number, post.step, post.warranty_request, side, user?.center?.is_lna]);

    useEffect(() => {
        if (!selectedRegexParts.AB || !selectedRegexParts.the_number) return;
        const serial = `${selectedRegexParts.AB}${selectedRegexParts.the_number}`;
        setRegexState(prev => prev.map(i => i.field === "serial" ? { ...i, result: serial } : i));
        if (meta?.use_OCR) dispatch(patchMeta(meta.name, serial));
    }, [dispatch, meta?.use_OCR, meta?.name, selectedRegexParts.AB, selectedRegexParts.the_number]);

    useEffect(() => {
        const [regexConfig, transforms] = getRegex(user, side, meta, post.item_number, post.valve_sticker_variant);
        setRegexState(regexConfig);
        setTransformations(transforms);
        regexLengthRef.current = regexConfig.length;
    }, [side, user, meta, post.item_number, post.valve_sticker_variant]);

    const keep_trying_or_click_image = regex.filter(i => !i.result).length > 0;
    const take_picture_and_continue = regex.filter(i => !i.result).length === 0 || meta?.picture_only;

    const resetRegex = useCallback((regexItem) => {
        setRegexState(prev => prev.map(i => i.field === regexItem.field ? { ...i, result: false, scanned: false } : i));
    }, []);

    return {
        canvasRef,
        regex,
        setRegexState,
        regexParts,
        selectedRegexParts,
        setSelectedRegexParts,
        camera_results,
        jobQueue,
        processedImage,
        transformations,
        resetRegex,
        keep_trying_or_click_image,
        take_picture_and_continue,
        isLabel,
        labels,
        post,
        user,
        meta,
        isCordCylinder,
    };
}
